import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MaintenanceDetails } from '../../../interface/partners-maintenance-scheduling.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slices/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ConfirmationWarning from '../../../shared-components/disable-warning/DisableWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import ConvertToUTC from '../../../shared-functions/ConvertToUTC';
import maintenanceSchedule from '../../../utils/api/v2/maintenance-schedule';

interface MaintenanceContentProperties {
  maintenanceDetails: MaintenanceDetails;
  onChangeValue: (name: string, value: string | number | string[]) => void;
  openSuccessModal: () => void;
  closeModal: () => void;
  allStationsList?: { label: string; value: string | number }[];
}

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Show warning when navigate back
 * @param {any} param0 - The param0
 * @param {any} param0.maintenanceDetails - The maintenanceDetails prop
 * @param {any} param0.onChangeValue - The onChangeValue prop
 * @param {any} param0.closeModal - The closeModal prop
 * @param {any} param0.openSuccessModal - The openSuccessModal prop
 * @param {any} param0.allStationsList - The allStationsList prop
 */
function MaintenanceContent({
  maintenanceDetails,
  onChangeValue,
  closeModal,
  openSuccessModal,
  allStationsList,
}: MaintenanceContentProperties) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('maintenanceSchedule');
  const { partnerStations } = useAppSelector((state) => state.partnersApprovedStation);
  const { allDevices } = useAppSelector((state) => state.partnersDevice);
  const stationsToUse =
    allStationsList && allStationsList.length > 0 ? allStationsList : partnerStations;

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    header: false,
    content: null,
    notificationModal: false,
  });

  const [formStatus, setFormStatus] = useState({
    buttonLoader: false,
  });

  /**
   * Handle form submission and open modal with MaintenanceContent
   */
  const handleNavigateBack = () => {
    closeModal(); // Close the modal when navigating back
  };

  /**
   * Cancel schedule and create maintenance schedule
   */
  const handleCancelExistingSchedules = () => {
    const selectedStationIds = allDevices
      .filter((device) => maintenanceDetails.device.includes(device.value.toString()))
      .map((device) => device.stationId)
      .filter((stationId, index, self) => stationId && self.indexOf(stationId) === index);

    const request = {
      chargingStation: selectedStationIds.join(', '),
      chargingPoint: maintenanceDetails.device.join(', '),
      startDateUtc: ConvertToUTC(maintenanceDetails.time, maintenanceDetails.fromDate),
      endDateUtc: ConvertToUTC(maintenanceDetails.endTime, maintenanceDetails.endDate),
      type: maintenanceDetails.maintenance,
      priority: maintenanceDetails.priority,
    };
    maintenanceSchedule
      .cancelScheduleAndAddMaintenanceSchedule(request)
      .then(() => {
        openSuccessModal();
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Form submit function
   */
  const submitForm = () => {
    setFormStatus({
      buttonLoader: true,
    });

    const selectedStationIds = allDevices
      .filter((device) => maintenanceDetails.device.includes(device.value.toString()))
      .map((device) => device.stationId)
      .filter((stationId, index, self) => stationId && self.indexOf(stationId) === index);

    const request = {
      chargingStation: selectedStationIds.join(', '),
      chargingPoint: maintenanceDetails.device.join(', '),
      startDateUtc: ConvertToUTC(maintenanceDetails.time, maintenanceDetails.fromDate),
      endDateUtc: ConvertToUTC(maintenanceDetails.endTime, maintenanceDetails.endDate),
      type: maintenanceDetails.maintenance,
      priority: maintenanceDetails.priority,
    };
    maintenanceSchedule
      .addMaintenanceSchedule(request)
      .then(() => {
        setFormStatus({
          buttonLoader: false,
        });
        openSuccessModal();
      })
      .catch((error) => {
        setFormStatus({
          buttonLoader: false,
        });
        if (error?.response?.data?.message === 'A confirmed Scheduled Charging Session detected') {
          setModalBoxStatus('open');
          setModalBoxDetail({
            title: '',
            header: false,
            content: (
              <ConfirmationWarning
                selectedId=""
                onCancel={handleNavigateBack}
                message={
                  <>
                    {t('scheduledChargingSessionDetected')} <br />{' '}
                    {t('scheduledChargingSessionDetectedDescription')}
                  </>
                }
                cancelLabel={t('reschedule')}
                confirmLabel={t('proceed')}
                onConfirm={handleCancelExistingSchedules}
              />
            ),
            notificationModal: true,
          });
        } else {
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || t('somethingWentWrong'),
              },
            }),
          );
        }
      });
  };

  return (
    <div className="content-maintenance-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="content-maintenance-header">
        <h3>{t('maintenanceSchedule')}</h3>
      </div>
      <div className="content-maintenance-container">
        <div className="content-maintenance-from-wrap">
          <div className="content-maintenance-from">
            <CustomInput
              inputLabel={t('stationName')}
              type="text"
              placeholder=""
              name="name"
              value={(() => {
                const selectedStationIds = allDevices
                  .filter((device) => maintenanceDetails.device.includes(device.value.toString()))
                  .map((device) => device.stationId);
                return stationsToUse
                  .filter((station) => selectedStationIds.includes(station.value.toString()))
                  .map((station) => station.label)
                  .join(', ');
              })()}
              onChangeValue={onChangeValue}
              readOnly
            />
            <CustomInput
              inputLabel={t('device')}
              type="text"
              placeholder=""
              name="device"
              value={allDevices
                .filter((device) => maintenanceDetails.device.includes(device.value.toString()))
                .map((device) => device.label)
                .join(', ')}
              onChangeValue={onChangeValue}
              readOnly
            />
            <CustomInput
              name="fromDate"
              inputLabel={t('startDate', 'Start Date')}
              placeholder=""
              type="text"
              value={maintenanceDetails.fromDate}
              onChangeValue={onChangeValue}
              readOnly
            />
            <CustomInput
              name="time"
              inputLabel={t('startTime', 'Start Time')}
              placeholder=""
              type="text"
              value={maintenanceDetails.time}
              onChangeValue={onChangeValue}
              readOnly
            />
            <CustomInput
              name="endDate"
              inputLabel={t('endDate', 'End Date')}
              placeholder=""
              type="text"
              value={maintenanceDetails.endDate}
              onChangeValue={onChangeValue}
              readOnly
            />
            <CustomInput
              name="endTime"
              inputLabel={t('endTime', 'End Time')}
              placeholder=""
              type="text"
              value={maintenanceDetails.endTime}
              onChangeValue={onChangeValue}
              readOnly
            />
            <CustomInput
              inputLabel={t('typeOfMaintenance')}
              type="text"
              placeholder=""
              name="maintenance"
              value={maintenanceDetails.maintenance}
              onChangeValue={onChangeValue}
              readOnly
            />
            <CustomInput
              inputLabel={t('priority')}
              type="text"
              placeholder=""
              name="priority"
              value={maintenanceDetails.priority}
              onChangeValue={onChangeValue}
              readOnly
            />
          </div>
        </div>
        <div className="content-maintenance-button-wrap">
          <div className="form-buttons-wrap">
            <MyButton label={t('cancel')} buttonType="back" onClickFunc={handleNavigateBack} />
            <MyButton
              label={t('confirm')}
              buttonType="submit"
              onClickFunc={submitForm}
              disableHoverEffect
              loading={formStatus.buttonLoader}
              disableButton={formStatus.buttonLoader}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

MaintenanceContent.defaultProps = {
  allStationsList: [],
};

export default MaintenanceContent;
